import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { InicioPage } from './pages/InicioPage';
import ProductosPage from './pages/ProductosPage';
import ServiciosPage from './pages/ServiciosPage';
import ContactoPage from './pages/ContactoPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import { OrderTrackingPage } from './pages/OrderTrackingPage';
import { POSPage } from './pages/POSPage';
import { CierreCajaPage } from './pages/CierreCajaPage';
import ReportesTabsPage from './pages/mi-farmacia/ReportesTabsPage';
import ProductosGestionPage from './pages/ProductosGestionPage';
import KardexLotesPage from './pages/KardexLotesPage';
import LoginPage from './pages/LoginPage';
import { RegistroPage } from './pages/RegistroPage';
import { RegistroExitosoPage } from './pages/RegistroExitosoPage';
import { VerificarCuentaPage } from './pages/VerificarCuentaPage';
import DashboardPage from './pages/DashboardPage';
import ClientesPage from './pages/ClientesPage';
import UsuariosPage from './pages/UsuariosPage';
import ProveedoresPage from './pages/ProveedoresPage';
import { RolesPage } from './pages/security/RolesPage';
import { ModulesPage } from './pages/security/ModulesPage';
import { PermissionsPage } from './pages/security/PermissionsPage';
import { AuditoriaPage } from './pages/security/AuditoriaPage';
import CajaPage from './pages/CajaPage';
import ProductosVendidosPage from './pages/ProductosVendidosPage';
import NominaPage from './pages/NominaPage';
import Layout from './components/layout/Layout';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { RouteGuard } from './components/layout/RouteGuard';
import { ClienteLayout } from './components/layout/ClienteLayout';
import { ClienteDashboardPage } from './pages/ClienteDashboardPage';
import { SeguimientoPedidosPage } from './pages/cliente/SeguimientoPedidosPage';
import { HistorialPedidosPage } from './pages/cliente/HistorialPedidosPage';
import { ComprasClientePage } from './pages/cliente/ComprasClientePage';
import { FavoritosPage } from './pages/cliente/FavoritosPage';
import ClienteCheckoutPage from './pages/cliente/ClienteCheckoutPage';

// Crear cliente de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Helper para verificar si el usuario está autenticado
const isAuthenticated = () => {
  const token = localStorage.getItem('accessToken');
  const userData = localStorage.getItem('userData');
  return !!(token && userData);
};

// Helper para obtener el tipo de usuario
const getUserType = () => {
  const userData = localStorage.getItem('userData');
  if (userData) {
    const user = JSON.parse(userData);
    // Revisar diferentes posibles campos para identificar el tipo de usuario
    console.log('Datos del usuario:', user); // Para debug
    
    // Si tiene explícitamente marcado como cliente, ir al dashboard de cliente
    if (user.tipo === 'cliente' || user.role === 'cliente' || user.tipoUsuario === 'cliente') {
      return 'cliente';
    }
    
    // Por defecto, asumir que es administrador (especialmente para usuarios existentes)
    return 'admin';
  }
  return null;
};

// Componente para redirigir a dashboard según tipo de usuario
const DashboardRedirect = () => {
  const userType = getUserType();
  
  if (userType === 'cliente') {
    return <Navigate to="/cliente" replace />;
  } else {
    // Por defecto, ir al dashboard de administrador
    return <Navigate to="/dashboard" replace />;
  }
};

// Componente para proteger rutas públicas (redirigir a dashboard si está logueado)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  return isAuthenticated() ? <DashboardRedirect /> : <>{children}</>;
};

// Componente para proteger rutas privadas (redirigir a login si no está logueado)
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Rutas públicas protegidas */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/registro" element={
            <PublicRoute>
              <RegistroPage />
            </PublicRoute>
          } />
          <Route path="/registro-exitoso" element={
            <PublicRoute>
              <RegistroExitosoPage />
            </PublicRoute>
          } />
          <Route path="/verificar-cuenta" element={
            <PublicRoute>
              <VerificarCuentaPage />
            </PublicRoute>
          } />
          
          {/* Rutas del dashboard de administradores */}
          <Route path="/dashboard/*" element={
            <PrivateRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<RouteGuard moduleName="Dashboard"><DashboardPage /></RouteGuard>} />
                  <Route path="ventas" element={<RouteGuard moduleName="Ventas"><OrdersPage /></RouteGuard>} />
                  <Route path="pedidos" element={<Navigate to="/dashboard/ventas" replace />} />
                  <Route path="clientes" element={<RouteGuard moduleName="Clientes"><ClientesPage /></RouteGuard>} />
                  <Route path="membresia" element={<Navigate to="/dashboard/clientes" replace />} />
                  <Route path="membresias" element={<Navigate to="/dashboard/clientes" replace />} />
                  <Route path="administradores" element={<RouteGuard moduleName="Administradores"><UsuariosPage /></RouteGuard>} />
                  <Route path="seguridad/roles" element={<RouteGuard moduleName="Roles"><RolesPage /></RouteGuard>} />
                  <Route path="seguridad/modulos" element={<RouteGuard moduleName="Módulos"><ModulesPage /></RouteGuard>} />
                  <Route path="seguridad/permisos" element={<RouteGuard moduleName="Permisos"><PermissionsPage /></RouteGuard>} />
                  <Route path="seguridad/auditoria" element={<RouteGuard moduleName="Auditorías"><AuditoriaPage /></RouteGuard>} />
                  <Route path="mi-farmacia/caja" element={<RouteGuard moduleName="Caja"><CajaPage /></RouteGuard>} />
                  <Route path="mi-farmacia/productos-vendidos" element={<Navigate to="/dashboard/mi-farmacia/reportes?tab=productos-vendidos" replace />} />
                  <Route path="mi-farmacia/nomina" element={<RouteGuard moduleName="Nomina"><NominaPage /></RouteGuard>} />
                  <Route path="pos" element={<RouteGuard moduleName="Punto de Venta"><POSPage /></RouteGuard>} />
                  <Route path="cierre-caja" element={<RouteGuard moduleName="Cierre de Caja"><CierreCajaPage /></RouteGuard>} />
                  <Route path="mi-farmacia/reportes" element={<RouteGuard moduleName="Reportes"><ReportesTabsPage /></RouteGuard>} />
                  <Route path="reportes" element={<Navigate to="/dashboard/mi-farmacia/reportes" replace />} />
                  <Route path="productos" element={<RouteGuard moduleName="Productos"><ProductosGestionPage /></RouteGuard>} />
                  <Route path="kardex-lotes" element={<RouteGuard moduleName="Productos"><KardexLotesPage /></RouteGuard>} />
                  <Route path="proveedores" element={<RouteGuard moduleName="Proveedores"><ProveedoresPage /></RouteGuard>} />
                  {/* Ruta comodín para mantener al usuario en el dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </DashboardLayout>
            </PrivateRoute>
          } />
          
          {/* Rutas del dashboard de clientes */}
          <Route path="/cliente/*" element={
            <PrivateRoute>
              <ClienteLayout>
                <Routes>
                  <Route path="/" element={<ClienteDashboardPage />} />
                  <Route path="compras" element={<ComprasClientePage />} />
                  <Route path="seguimiento" element={<SeguimientoPedidosPage />} />
                  <Route path="historial" element={<HistorialPedidosPage />} />
                  <Route path="pedidos" element={<SeguimientoPedidosPage />} />
                  <Route path="favoritos" element={<FavoritosPage />} />
                  <Route path="checkout" element={<Navigate to="/productos" replace />} />
                  {/* Ruta comodín para mantener al usuario en el dashboard de cliente */}
                  <Route path="*" element={<Navigate to="/cliente" replace />} />
                </Routes>
              </ClienteLayout>
            </PrivateRoute>
          } />
          
          {/* Rutas públicas con layout */}
          <Route path="/*" element={
            <Layout>
              <Routes>
                <Route path="/" element={<InicioPage />} />
                <Route path="/productos" element={<ProductosPage />} />
                <Route path="/servicios" element={<ServiciosPage />} />
                <Route path="/contacto" element={<ContactoPage />} />
                <Route path="/checkout" element={<Navigate to="/productos" replace />} />
                <Route path="/pedidos" element={
                  isAuthenticated() ? <Navigate to="/cliente/seguimiento" replace /> : <OrderTrackingPage />
                } />
                {/* Redireccionar rutas antiguas de iglesia al dashboard */}
                <Route path="/nosotros" element={<Navigate to="/dashboard" replace />} />
                <Route path="/eventos" element={<Navigate to="/dashboard" replace />} />
                <Route path="/recursos" element={<Navigate to="/dashboard" replace />} />
                <Route path="/donaciones" element={<Navigate to="/dashboard" replace />} />
                <Route path="/fundacion" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          } />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
